<template>
    <div id="stats" class="portrait-stats s-portrait">
        <!-- 1. pick who: a team (its header) or one of its players -->
        <div class="s-teams">
            <div v-for="team in teams" :key="`s-team-${team.side}`" class="s-team-col">
                <button
                    type="button"
                    class="s-team-pick"
                    :class="{ 's-team-pick--selected': selection.side === team.side && !selection.playerId }"
                    :style="{ backgroundColor: team.color, color: team.textColor }"
                    @click="$emit('select-team', team.side)"
                >
                    <span class="s-team-pick__name">{{ team.name }}</span>
                    <span class="s-team-pick__hint">team</span>
                </button>
                <div
                    v-for="item in team.court"
                    :key="item.player.id"
                    class="p-player s-player"
                    :class="{ 's-player--selected': selection.playerId === item.player.id }"
                    @click="$emit('player-click', item.player, team.side)"
                >
                    <div class="p-player__jersey" :style="{ backgroundColor: team.color, color: team.textColor }">
                        {{ item.jersey }}
                        <v-icon v-if="item.mvp" x-small :color="team.textColor" class="p-player__mvp">mdi-crown</v-icon>
                    </div>
                    <div class="p-player__photo">
                        <v-icon color="grey"> mdi-account </v-icon>
                    </div>
                    <div class="p-player__stats">
                        <div class="p-player__points">{{ item.points }}<small>pts</small></div>
                        <div class="p-player__line">
                            {{ item.rebounds }}r · {{ item.assists }}a
                            <span class="p-player__fouls" :class="item.foulClass">{{ item.fouls }}f</span>
                        </div>
                    </div>
                </div>
                <div v-if="!team.court.length" class="p-court-empty">No players on court</div>
            </div>
        </div>

        <!-- 2. then what: the stat for the selection -->
        <div class="s-actions-card">
            <div class="sd-grid sd-grid--2 s-shots">
                <v-btn
                    v-for="action in shotActions"
                    :key="action.key"
                    class="sd-stat-btn s-btn s-btn--shot"
                    :class="{ 'sd-stat-btn--missed': action.key === 'missed' }"
                    depressed
                    :disabled="!action.enabled"
                    v-bind="action.key === 'missed' ? { style: { borderColor: actionColor } } : { color: actionColor, dark: !actionLight, light: actionLight }"
                    @click="$emit('action', action.key)"
                >
                    <v-icon left :color="action.key === 'missed' ? 'red' : undefined">{{ action.icon }}</v-icon>{{ action.label }}
                </v-btn>
            </div>
            <div class="sd-grid sd-grid--2 s-others">
                <v-btn
                    v-for="action in otherActions"
                    :key="action.key"
                    class="sd-stat-btn sd-stat-btn--other s-btn"
                    depressed
                    :disabled="!action.enabled"
                    :color="actionColor"
                    :dark="!actionLight"
                    :light="actionLight"
                    @click="$emit('action', action.key)"
                >
                    <v-icon left small>{{ action.icon }}</v-icon>{{ action.label }}
                </v-btn>
            </div>
        </div>

        <div class="s-section">
            <div class="p-bench-toggles">
                <stats-bench-toggle
                    v-for="team in teams"
                    :key="`s-bench-toggle-${team.side}`"
                    :team="team"
                    :open="openBenchSide === team.side"
                    @toggle="$emit('toggle-bench', team.side)"
                />
            </div>
            <v-expand-transition>
                <stats-bench-panel
                    v-if="openTeam"
                    :team="openTeam"
                    :selected-player-id="selection.playerId"
                    @close="$emit('toggle-bench', openTeam.side)"
                    @player-click="$emit('player-click', $event, openTeam.side)"
                />
            </v-expand-transition>
        </div>
        <div class="s-section">
            <slot name="feed" />
        </div>
        <div class="s-section s-section--last">
            <v-btn block large outlined color="red darken-1" class="p-end-match" @click="$emit('end-match')">
                <v-icon left>mdi-flag-checkered</v-icon>End match
            </v-btn>
        </div>

        <!-- always visible: what the next stat goes to -->
        <div class="s-selection-dock">
            <slot name="selection" />
        </div>
    </div>
</template>

<script>
    import StatsBenchToggle from '@/components/stats/StatsBenchToggle'
    import StatsBenchPanel from '@/components/stats/StatsBenchPanel'

    // "Select first" layout for phones and tablets held upright: tap a team or player, then the stat.
    // Presentational: the page passes the team views, the selection and the actions (with enabled flags).
    export default {
        name: 'StatsSelectPortrait',
        components: { StatsBenchToggle, StatsBenchPanel },
        props: {
            teams: {
                type: Array,
                required: true
            },
            selection: {
                type: Object,
                default: () => ({ side: null, playerId: null })
            },
            actions: {
                type: Array,
                default: () => []
            },
            actionColor: {
                type: String,
                default: '#9e9e9e'
            },
            actionLight: {
                type: Boolean,
                default: false
            },
            openBenchSide: {
                type: Number,
                default: null
            }
        },
        computed: {
            openTeam() {
                return this.teams.find(team => team.side === this.openBenchSide) || null
            },
            shotActions() {
                return this.actions.filter(action => action.key === 'made' || action.key === 'missed')
            },
            otherActions() {
                return this.actions.filter(action => action.key !== 'made' && action.key !== 'missed')
            }
        }
    }
</script>
